import React, { useState } from 'react';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from 'react-query';

import { trackEvent } from 'jf/analytics/Analytics';
import { MsTeamsTargetingClient, MSTeamsUser } from 'jf/api';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExLink } from 'jf/components/DevExLink';
import { DevExText } from 'jf/components/DevExText';
import { MSTeamsSearchBar } from 'jf/pages/studyBuilder/targetRespondents/msteams/MSTeamsSearchBar';
import { useStudyMSTeamsTargetUpdater } from 'jf/pages/studyBuilder/targetRespondents/msteams/useStudyMSTeamsTargetUpdater';
import { useTargetingClient } from 'jf/services/useTargetingClient';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

import { MSTeamsTargetTags } from './MSTeamsTargetTags';

type ManageMSTeamsRespondentsCardProps = {
  studyRef: string;
};

export const ManageMSTeamsRespondentsCard: React.FC<ManageMSTeamsRespondentsCardProps> = ({
  studyRef,
}) => {
  const { msTeamsTargeting, slackTargeting } = useClientFlags();
  const [isEditing, setIsEditing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [unsavedMsteamsTargets, setUnsavedMsteamsTargets] = useState<MSTeamsUser[]>([]);
  const [showRespondents, setShowRespondents] = useState(false);
  const queryClient = useQueryClient();

  const studyMSTeamsTargetUpdater = useStudyMSTeamsTargetUpdater(studyRef);
  const { data: msteamsTargets, refetch } = useClientQuery(
    MsTeamsTargetingClient.getStudyMsteamsTargets,
    { studyRef: studyRef },
    { enabled: slackTargeting && msTeamsTargeting }
  );
  const { mutateAsync: expandMsteamsEntity, isLoading } = useClientMutation(
    MsTeamsTargetingClient.expandMsteamsEntity
  );
  const { mutateAsync: sendStudyMessages } = useClientMutation(
    MsTeamsTargetingClient.sendStudyMsteamsMessages
  );
  const { msgProviderLower } = useTargetingClient();

  const msteamsTargetCount = msteamsTargets?.length || 0;
  const msteamsTargetUsers = msteamsTargets?.map((target) => target as MSTeamsUser) || [];

  const onSend = async () => {
    setIsSending(true);
    // I'm taking a gamble here that getStudyMSTeamsTargets() returns the same
    // MSTeamsUser list as getStudyTargets({msTeamsSelected: true}), so we can
    // skip the getStudyTargets query
    const targetsToSave = [...msteamsTargetUsers, ...unsavedMsteamsTargets];
    if (unsavedMsteamsTargets.length) {
      await studyMSTeamsTargetUpdater.update(targetsToSave);
    }

    await sendStudyMessages({ studyRef: studyRef!, inviteOnly: true });
    setUnsavedMsteamsTargets([]);
    refetch();
    queryClient.invalidateQueries(['GET_STUDY', studyRef]);

    trackEvent(`survey-analyzer:manage-${msgProviderLower}-targets:send`);

    setIsSending(false);
    setIsEditing(false);
  };

  return (
    <DevExCard
      title={
        <DevExText fontSize="md" strong>
          Survey Distribution
        </DevExText>
      }
      extra={
        isEditing ? (
          <>
            <DevExButton
              onClick={() => {
                setIsEditing(false);
                setUnsavedMsteamsTargets([]);
              }}
            >
              Cancel
            </DevExButton>
            <DevExButton
              type="primary"
              disabled={isSending || !unsavedMsteamsTargets.length}
              onClick={() => onSend()}
            >
              Send to {unsavedMsteamsTargets.length} new respondent
              {unsavedMsteamsTargets.length === 1 ? '' : 's'}
            </DevExButton>
          </>
        ) : (
          <DevExButton
            icon={<FontAwesomeIcon icon={faPen} />}
            type="outline"
            onClick={() => setIsEditing(true)}
          />
        )
      }
    >
      {!msteamsTargetCount ? (
        <DevExText>The survey was sent via link.</DevExText>
      ) : (
        <>
          <DevExText lined>
            The survey was sent individually to{' '}
            <DevExText strong>{msteamsTargetCount} people </DevExText>
            in MS Teams.
          </DevExText>
          <DevExLink onClick={() => setShowRespondents(!showRespondents)}>
            {showRespondents ? 'Hide' : 'Show'} {msteamsTargetCount} recipeient
            {msteamsTargetCount === 1 ? '' : 's'}
          </DevExLink>
          {isEditing && (
            <MSTeamsSearchBar
              setSelectedMsteamsUsers={setUnsavedMsteamsTargets}
              expandMsteamsEntity={expandMsteamsEntity}
            />
          )}

          {showRespondents && (
            <MSTeamsTargetTags
              msteamsTargets={msteamsTargetUsers}
              unsavedMsteamsUsers={unsavedMsteamsTargets}
              onChange={setUnsavedMsteamsTargets}
              isExpanding={isLoading}
            />
          )}
        </>
      )}
    </DevExCard>
  );
};
